/* ═══════════════════════════════════════════════════════════════
   The Designer — Design-mode palette bodies.

   Nothing here is a new primitive: every body is the SAME component
   the classic forma view docks (LevelProps, DocSettings, TokenRail,
   ProjectTokenChips + FormaPlaceholderBindings, DropZone chips) —
   re-hosted over the Designer canvas and writing through the forma
   draft instead of app.tsx state. The classic view keeps its own
   copies untouched.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import type { EmbeddedImage, StandardConfig } from '../../shared/types';
import { caretLevelOf } from './ToolbarControls';
import { DocSettings } from '../components/EditorComponents';
import { LevelProps } from '../components/LevelProps';
import { DropZone, ProjectTokenChips, TokenRail } from '../components/TokenComponents';
import { FormaPlaceholderBindings } from '../components/FormaPlaceholderBindings';
import {
  nextPlaceholderToken,
  usedPlaceholderIds,
  ISSUANCE_DATE_ID,
  type PlaceholderContext,
} from '../../shared/forma-placeholders';
import { defaultNumberFormats } from '../utils';
import { isCsiDefault, resetLevelToCsi } from '../../shared/csi-defaults';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  BAND_LABEL,
  COLUMN_LABEL,
  type BandKind,
  type PageVariant,
  type ZoneColumn,
  zoneKeyFor,
  readZone,
  zoneInsertAt,
  zoneRemoveAt,
  zoneReplaceAt,
  zoneUpdateAt,
  zoneAppend,
} from './formaZonesModel';

export type FormaEdit = (fn: (f: StandardConfig) => StandardConfig) => void;

const PALETTE_HINT: React.CSSProperties = {
  padding: 'var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1.55,
  color: 'var(--color-text-muted)',
};

const PALETTE_HOST: React.CSSProperties = {
  width: '100%',
  height: '100%',
  overflow: 'auto',
  background: 'var(--sf)',
};

/* ─── Levels — a sub-tool, not a mode ───
   By the time a spec is in a project its levels are already defined, so this
   palette is something you reach for: it follows the caret's level by default
   and lets you point it anywhere with the 1–9 picker. Nothing about clicking
   body text changes intent — that always just places the caret. */

export function LevelPalette({
  forma,
  editor,
  levelFocus,
  onLevel,
  onEdit,
  disabled,
}: {
  forma: StandardConfig;
  /** The live document — the palette follows the caret's level by default. */
  editor: Editor | null;
  /** Non-null when the user pointed the palette at a level deliberately. */
  levelFocus: number | null;
  onLevel: (level: number) => void;
  onEdit: FormaEdit;
  disabled?: boolean;
}) {
  // Follow the caret without making the whole workspace re-render: only this
  // palette subscribes to the editor.
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('selectionUpdate', tick);
    return () => {
      editor.off('selectionUpdate', tick);
    };
  }, [editor]);

  const level = levelFocus ?? caretLevelOf(editor) ?? 1;
  const idx = Math.max(0, Math.min(8, level - 1));
  const target = forma.levels?.[idx];
  return (
    <div style={PALETTE_HOST}>
      <div className="dsg-levelpicker" role="group" aria-label="Level to style">
        <span className="dsg-levelpicker-label">Level</span>
        {Array.from({ length: 9 }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            className={`dsg-levelpicker-btn${n === level ? ' is-active' : ''}`}
            aria-pressed={n === level}
            onClick={() => onLevel(n)}
            title={`Style every level ${n} paragraph`}
          >
            {n}
          </button>
        ))}
      </div>
      {target ? (
        <>
          {/* CSI SectionFormat defaults (contract §3 R6, item 1) — visibly
             labeled as defaults, never silent: a lay person needs to know
             whether a spacing/indent value is the published standard or a
             firm decision before they touch it. */}
          <div className={`dsg-csi-banner${isCsiDefault(target) ? ' is-default' : ' is-custom'}`}>
            <span className="dsg-csi-banner-label">
              {isCsiDefault(target) ? 'CSI SectionFormat default' : 'Custom — differs from CSI'}
            </span>
            {!isCsiDefault(target) && (
              <button
                type="button"
                className="dsg-csi-reset-btn"
                disabled={disabled}
                onClick={() =>
                  onEdit((f) => {
                    const levels = [...f.levels];
                    levels[idx] = resetLevelToCsi(levels[idx]);
                    return { ...f, levels };
                  })
                }
                title="Put this level's spacing and indents back to the published CSI SectionFormat convention. Bold, caps, colour and justification are untouched — those are the firm's, not the standard's."
              >
                Reset to CSI default
              </button>
            )}
          </div>
          <fieldset className="dsg-palette-fieldset" disabled={disabled}>
            <LevelProps
              level={target}
              levelIdx={idx}
              onUpdate={(u: unknown) =>
                onEdit((f) => {
                  const levels = [...f.levels];
                  levels[idx] = u as (typeof levels)[number];
                  return { ...f, levels };
                })
              }
              numFormats={forma.special?.numberFormats || defaultNumberFormats()}
              onNumFmtChange={(i: number, fmt: string) =>
                onEdit((f) => {
                  const nf = [...(f.special?.numberFormats || defaultNumberFormats())];
                  nf[i] = fmt;
                  return { ...f, special: { ...f.special, numberFormats: nf } };
                })
              }
            />
          </fieldset>
        </>
      ) : (
        <div style={PALETTE_HINT}>
          This forma does not define level {level} yet. Pick another level, or add it in the master
          forma.
        </div>
      )}
    </div>
  );
}

/* ─── Layout Options — the same DocSettings section the classic view docks ─── */

export function LayoutPalette({
  forma,
  onEdit,
  pageVariant,
  onPageVariant,
}: {
  forma: StandardConfig;
  onEdit: FormaEdit;
  pageVariant: PageVariant;
  onPageVariant: (v: PageVariant) => void;
}) {
  return (
    <div style={PALETTE_HOST}>
      <DocSettings
        doc={forma.docSettings}
        onUpdate={(d: StandardConfig['docSettings']) => onEdit((f) => ({ ...f, docSettings: d }))}
        sections={['Layout Options']}
        pageVariant={pageVariant}
        onPageVariant={(v: string) => onPageVariant(v as PageVariant)}
      />
    </div>
  );
}

/* ─── Tokens — the shared firm token rail ─── */

export function TokensPalette({
  savedTokens,
  recentTokens,
  onSaveToken,
  onRemoveSavedToken,
  onDragState,
  onInsert,
}: {
  savedTokens: string[];
  recentTokens: string[];
  onSaveToken: (t: string) => void;
  onRemoveSavedToken: (t: string) => void;
  onDragState: (on: boolean) => void;
  onInsert?: (value: string) => void;
}) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--sf)',
        overflow: 'hidden',
      }}
    >
      <TokenRail
        savedTokens={savedTokens}
        recentTokens={recentTokens}
        onSave={onSaveToken}
        onRemoveSaved={onRemoveSavedToken}
        onStart={() => onDragState(true)}
        onEnd={() => onDragState(false)}
        onInsert={onInsert}
      />
    </div>
  );
}

/* ─── Project Tokens — placeholder chips + the binding list ─── */

export function ProjectTokensPalette({
  forma,
  ctx,
  projectTokens,
  issuanceNames,
  onEdit,
  onDragState,
  onInsert,
  masterAuthoring,
}: {
  forma: StandardConfig;
  ctx: PlaceholderContext;
  projectTokens: Array<{ id: string; name: string; content: string }>;
  issuanceNames: string[];
  onEdit: FormaEdit;
  onDragState: (on: boolean) => void;
  onInsert?: (value: string) => void;
  /** True in the Master Forma Studio: the user is AUTHORING the master, so
   *  the hint explains defining {P#} blanks — not the Designer's "still
   *  prints with a master" copy, which describes a PROJECT spec that hasn't
   *  forked its own forma yet and is nonsense in the Studio (Josh,
   *  2026-08-13). */
  masterAuthoring?: boolean;
}) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--sf)',
        overflow: 'hidden',
      }}
    >
      <ProjectTokenChips
        nextPlaceholder={nextPlaceholderToken(forma)}
        usedPlaceholders={usedPlaceholderIds(forma).filter((id) => id !== ISSUANCE_DATE_ID)}
        projectTokens={projectTokens}
        onStart={() => onDragState(true)}
        onEnd={() => onDragState(false)}
        onInsert={onInsert}
      />
      {forma.projectId ? (
        <FormaPlaceholderBindings
          config={forma}
          ctx={ctx}
          issuanceNames={issuanceNames}
          onChange={(next) => onEdit((f) => ({ ...f, bindings: next }))}
        />
      ) : masterAuthoring ? (
        <div style={PALETTE_HINT}>
          A master defines the <strong style={{ color: 'var(--tx)' }}>blanks</strong>. Drag the
          Placeholder chip into a header or footer wherever this client&rsquo;s templates carry
          project-specific text — each {'{P#}'} remembers what was there. Every project that uses
          this master fills its blanks with that project&rsquo;s own values.
        </div>
      ) : (
        <div style={PALETTE_HINT}>
          This spec still prints with a <strong style={{ color: 'var(--tx)' }}>master</strong>{' '}
          forma. The first formatting change you make forks this project&rsquo;s own copy — then
          each placeholder gets its value here.
        </div>
      )}
    </div>
  );
}

/* ─── Header & Footer — the selected band's three columns, chip-editable ─── */

export interface TextDialogRequest {
  title: string;
  initial: string;
  onCommit: (value: string) => void;
  /** Show a date picker that writes the chosen date into the text, formatted
   *  long-form — the classic canvas's "Use Date" modal, which is how a FIXED
   *  (frozen) date gets stamped into a zone. Distinct from the live `{Date}`
   *  token, which re-resolves every export. */
  datePicker?: boolean;
}

export function ZonePalette({
  forma,
  band,
  variant,
  onEdit,
  images,
  onRequestText,
  onRequestImage,
  onCustomTokenUsed,
}: {
  forma: StandardConfig;
  band: BandKind | null;
  variant: PageVariant;
  onEdit: FormaEdit;
  images: Record<string, EmbeddedImage>;
  /** Open the shared literal-text dialog (portalled, Esc-dismissable). */
  onRequestText: (req: TextDialogRequest) => void;
  /** Pick an image from disk → returns `{Image:id}` or null. */
  onRequestImage: () => Promise<string | null>;
  onCustomTokenUsed: (t: string) => void;
}) {
  if (!band) {
    return (
      <div style={PALETTE_HOST}>
        <div style={PALETTE_HINT}>
          Click the header, footer, or a band line on the paper to edit it here. Drag token chips
          straight onto the paper&rsquo;s bands, or into the columns below.
        </div>
      </div>
    );
  }

  const variantLabel =
    band === 'pre' || band === 'post'
      ? ''
      : variant === 'first'
        ? ' · First page'
        : variant === 'even'
          ? ' · Even pages'
          : ' · Odd pages';

  const columns: ZoneColumn[] = ['L', 'C', 'R'];

  const insertValue = (zoneKey: string, idx: number | null, value: string, newLine?: boolean) => {
    if (value === '{Image}') {
      void onRequestImage().then((tok) => {
        if (!tok) return;
        onEdit((f) =>
          idx === null ? zoneAppend(f, zoneKey, tok) : zoneInsertAt(f, zoneKey, idx, tok)
        );
      });
      return;
    }
    onEdit((f) =>
      idx === null
        ? zoneAppend(f, zoneKey, value)
        : zoneInsertAt(f, zoneKey, idx, value, null, null, newLine)
    );
    if (!value.startsWith('{') && value !== '\n') onCustomTokenUsed(value);
  };

  return (
    <div style={PALETTE_HOST}>
      <div
        className="sf-palette-flow"
        style={{
          padding: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'var(--color-text-muted)',
          }}
        >
          {BAND_LABEL[band]}
          {variantLabel}
        </div>
        {columns.map((col) => {
          const zoneKey = zoneKeyFor(band, variant, col);
          const tokens = readZone(forma, zoneKey);
          return (
            <div key={col} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  color: 'var(--color-text-subtle)',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                {COLUMN_LABEL[col]}
              </span>
              <DropZone
                zoneKey={zoneKey}
                position={COLUMN_LABEL[col].toLowerCase()}
                tokens={tokens}
                visible
                fontSize={11}
                sizeAware
                special={forma.special}
                images={images}
                doc={forma.docSettings}
                fontFamily={forma.docSettings?.bodyFont}
                onDrop={(value, sourceZone, sourceIndex) => {
                  if (value === '__custom__') {
                    onRequestText({
                      title: 'Custom text',
                      initial: '',
                      onCommit: (text) => {
                        if (!text) return;
                        onEdit((f) => {
                          let next = f;
                          if (sourceZone != null && sourceIndex != null)
                            next = zoneRemoveAt(next, sourceZone, sourceIndex);
                          return zoneAppend(next, zoneKey, text);
                        });
                        onCustomTokenUsed(text);
                      },
                    });
                    return;
                  }
                  if (value === '{Image}' && sourceZone == null) {
                    insertValue(zoneKey, null, value);
                    return;
                  }
                  onEdit((f) => {
                    let next = f;
                    if (sourceZone != null && sourceIndex != null) {
                      next = zoneRemoveAt(next, sourceZone, sourceIndex);
                    }
                    return zoneAppend(next, zoneKey, value);
                  });
                }}
                onRemove={(idx) => onEdit((f) => zoneRemoveAt(f, zoneKey, idx))}
                onEditToken={(idx) => {
                  const cur = tokens[idx] || '';
                  // Literal text only — strip trailing |mods, reattach on save
                  // (same rule as the classic canvas).
                  const pipe = cur.lastIndexOf('|');
                  const looksToken = cur.startsWith('{') && cur.endsWith('}');
                  if (looksToken) return;
                  const base = pipe > 0 ? cur.slice(0, pipe) : cur;
                  const suffix = pipe > 0 ? cur.slice(pipe) : '';
                  onRequestText({
                    title: 'Edit text',
                    initial: base,
                    onCommit: (text) => onEdit((f) => zoneUpdateAt(f, zoneKey, idx, text + suffix)),
                  });
                }}
                onInsertAt={(idx, value, srcZ, srcI, newLine) => {
                  if (value === '__custom__') {
                    onRequestText({
                      title: 'Custom text',
                      initial: '',
                      onCommit: (text) => {
                        if (!text) return;
                        onEdit((f) =>
                          zoneInsertAt(f, zoneKey, idx, text, srcZ ?? null, srcI ?? null, newLine)
                        );
                        onCustomTokenUsed(text);
                      },
                    });
                    return;
                  }
                  if (value === '{Image}' && srcZ == null) {
                    void onRequestImage().then((tok) => {
                      if (!tok) return;
                      onEdit((f) => zoneInsertAt(f, zoneKey, idx, tok, null, null, newLine));
                    });
                    return;
                  }
                  onEdit((f) =>
                    zoneInsertAt(f, zoneKey, idx, value, srcZ ?? null, srcI ?? null, newLine)
                  );
                }}
                onReplaceAt={(idx, value, srcZ, srcI) => {
                  if (value === '__custom__') {
                    onRequestText({
                      title: 'Custom text',
                      initial: '',
                      onCommit: (text) => {
                        if (!text) return;
                        onEdit((f) =>
                          zoneReplaceAt(f, zoneKey, idx, text, srcZ ?? null, srcI ?? null)
                        );
                        onCustomTokenUsed(text);
                      },
                    });
                    return;
                  }
                  onEdit((f) => zoneReplaceAt(f, zoneKey, idx, value, srcZ ?? null, srcI ?? null));
                }}
                onUpdateToken={(idx, value) => onEdit((f) => zoneUpdateAt(f, zoneKey, idx, value))}
                onCustomDrop={(srcZ, srcI) => {
                  onRequestText({
                    title: 'Custom text',
                    initial: '',
                    onCommit: (text) => {
                      if (!text) return;
                      onEdit((f) => {
                        let next = f;
                        if (srcZ != null && srcI != null) next = zoneRemoveAt(next, srcZ, srcI);
                        return zoneAppend(next, zoneKey, text);
                      });
                      onCustomTokenUsed(text);
                    },
                  });
                }}
              />
            </div>
          );
        })}
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-subtle)',
            lineHeight: 1.5,
          }}
        >
          Drag chips between columns to move them; right-click a chip for formatting and
          copy/cut/paste.
        </div>
      </div>
    </div>
  );
}

/* ─── Shared literal-text dialog (portalled, Esc, focus on open) ─── */

export function TextTokenDialog({
  request,
  onClose,
}: {
  request: TextDialogRequest;
  onClose: () => void;
}) {
  const [value, setValue] = React.useState(request.initial);
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const commit = () => {
    // Free text: keep interior newlines, drop only surrounding blank space.
    const v = value.replace(/^\s+|\s+$/g, '');
    onClose();
    if (v) request.onCommit(v);
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={request.title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--color-bg) 55%, transparent)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 340,
          maxWidth: '90vw',
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-xl)',
          padding: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            color: 'var(--color-text-muted)',
          }}
        >
          {request.title}
        </span>
        {request.datePicker && (
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-1)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            Pick a date
            <Input
              type="date"
              aria-label="Pick a date to print"
              onChange={(e) => {
                if (!e.target.value) return;
                const d = new Date(e.target.value + 'T12:00:00');
                setValue(
                  d.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                );
              }}
            />
          </label>
        )}
        {/* FREE TEXT, not a one-line string (Josh, 2026-08-13). A header line
            is routinely multi-line — "Client Name / City, State" — and the
            zone model has always stored newlines. Enter makes a new line;
            Ctrl+Enter saves. */}
        <textarea
          ref={inputRef as unknown as React.RefObject<HTMLTextAreaElement>}
          className="sf-input dsg-freetext"
          value={value}
          rows={3}
          placeholder="Text to print — Enter for a new line"
          aria-label={request.datePicker ? 'Date as it will print' : 'Text to print'}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              commit();
            }
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={commit} disabled={!value.trim()} title="Ctrl+Enter">
            Save
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
